/**
 * Gross Profit — revenue against what we actually paid the supplier.
 * Day / week / month / quarter / year-to-date / custom range, with a QuickBooks
 * variance row and a list of orders that are still missing a supplier cost.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp, Download, Printer, RefreshCw, AlertTriangle, Loader2, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, useAuth } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import { canViewCost } from "@/lib/costAccess";

const fmt$ = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => (v == null ? "—" : `${v}%`);

const PRESETS = [
  { id: "day", label: "Daily" },
  { id: "week", label: "Weekly" },
  { id: "month", label: "Monthly" },
  { id: "quarter", label: "Quarterly" },
  { id: "ytd", label: "Year to date" },
  { id: "custom", label: "Custom range" },
];

const gpTone = (v) =>
  v == null ? "text-slate-400"
    : v >= 25 ? "text-emerald-700"
      : v >= 12 ? "text-amber-700"
        : "text-rose-700";

const Kpi = ({ label, value, sub, tone = "text-slate-900", testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</p>
    <p className={`text-2xl font-black mt-1 tabular-nums ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

export default function GrossProfitBoard() {
  const { token, user } = useAuth();
  const allowed = canViewCost(user);
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState({ start: "", end: "" });
  const [basis, setBasis] = useState("booked");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("period");

  const qs = useMemo(() => {
    const p = new URLSearchParams({ preset, basis });
    if (preset === "custom") { p.set("start", range.start); p.set("end", range.end); }
    return p.toString();
  }, [preset, basis, range.start, range.end]);

  const load = useCallback(async () => {
    if (!allowed) return;
    if (preset === "custom" && (!range.start || !range.end)) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/gross-profit?${qs}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the gross profit report");
    } finally {
      setLoading(false);
    }
  }, [qs, token, allowed, preset, range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  if (!allowed) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center"
        data-testid="gp-no-access">
        <Lock className="w-6 h-6 text-slate-400 mx-auto" />
        <h2 className="text-lg font-bold text-slate-900 mt-3">Cost and gross profit are restricted</h2>
        <p className="text-sm text-slate-500 mt-1">
          Ask a super admin to grant you the cost &amp; gross profit permission.
        </p>
      </div>
    );
  }

  const t = data?.totals;
  const q = data?.qbo;
  const rows = tab === "period" ? data?.periods
    : tab === "supplier" ? data?.by_supplier
      : tab === "hospital" ? data?.by_hospital
        : data?.orders;

  return (
    <div className="space-y-5" data-testid="gross-profit-board">
      {/* controls */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-black text-slate-900 inline-flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" /> Gross Profit
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Revenue billed against supplier cost. Freight sits outside both sides.
              {data && <span className="text-slate-400"> · {data.range.label} · {data.range.start} → {data.range.end}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load}
              data-testid="gp-refresh">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => openAuthedDoc(`/admin/gross-profit/export?${qs}`, { token, fmt: "csv", filename: "SterileMate-Gross-Profit.csv" })}
              data-testid="gp-export-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button size="sm" className="h-8 text-xs bg-slate-900 hover:bg-slate-800"
              onClick={() => openAuthedDoc(`/admin/gross-profit/export?${qs}`, { token, fmt: "pdf" })}
              data-testid="gp-export-pdf">
              <Printer className="w-3.5 h-3.5 mr-1" /> Bookkeeper PDF
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mt-4 flex-wrap">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => setPreset(p.id)}
              className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${
                preset === p.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid={`gp-preset-${p.id}`}>
              {p.label}
            </button>
          ))}
          <span className="w-px h-6 bg-slate-200 mx-1" />
          <button onClick={() => setBasis(basis === "booked" ? "all" : "booked")}
            className="h-8 px-3 rounded-full text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200"
            title="Booked = PO received through delivered. All = every live order including quotes."
            data-testid="gp-basis-toggle">
            {basis === "booked" ? "Booked orders" : "All live orders"}
          </button>
          {preset === "custom" && (
            <div className="flex items-center gap-1.5 ml-1">
              <Input type="date" className="h-8 w-36 text-xs" value={range.start}
                onChange={(e) => setRange({ ...range, start: e.target.value })}
                data-testid="gp-custom-start" />
              <span className="text-slate-400 text-xs">to</span>
              <Input type="date" className="h-8 w-36 text-xs" value={range.end}
                onChange={(e) => setRange({ ...range, end: e.target.value })}
                data-testid="gp-custom-end" />
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="gp-kpis">
          <Kpi label="Revenue" value={fmt$(t.revenue)} sub={`${t.order_count} orders`} testid="gp-kpi-revenue" />
          <Kpi label="Supplier cost" value={fmt$(t.cost)} sub="what we paid" testid="gp-kpi-cost" />
          <Kpi label="Gross profit" value={fmt$(t.gp)} tone="text-emerald-700" testid="gp-kpi-gp" />
          <Kpi label="GP %" value={pct(t.gp_pct)} tone={gpTone(t.gp_pct)} testid="gp-kpi-gp-pct" />
          <Kpi label="Missing cost" value={t.missing_cost_orders}
            tone={t.missing_cost_orders ? "text-rose-700" : "text-slate-900"}
            sub={t.missing_cost_orders ? "margin overstated" : "every order costed"}
            testid="gp-kpi-missing" />
        </div>
      )}

      {/* QBO match */}
      {q && (
        <div className={`rounded-xl border p-4 ${q.connected ? "border-sky-200 bg-sky-50/60" : "border-slate-200 bg-slate-50"}`}
          data-testid="gp-qbo-panel">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-sky-700">
            QuickBooks match
          </p>
          {q.connected ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
              {[["Income", q.income, q.variance_revenue], ["COGS", q.cogs, q.variance_cost],
                ["Gross profit", q.gross_profit, q.variance_gp]].map(([label, v, diff]) => (
                <div key={label}>
                  <p className="text-[11px] text-slate-500">{label}</p>
                  <p className="text-base font-bold tabular-nums text-slate-900">{fmt$(v)}</p>
                  <p className={`text-[11px] font-semibold ${Math.abs(Number(diff) || 0) < 0.01 ? "text-emerald-700" : "text-amber-700"}`}>
                    {Math.abs(Number(diff) || 0) < 0.01 ? "ties out" : `${fmt$(diff)} variance`}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-600 mt-1">{q.note}</p>
          )}
        </div>
      )}

      {/* missing cost */}
      {!!data?.missing_cost?.length && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4" data-testid="gp-missing-panel">
          <p className="text-sm font-bold text-amber-900 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            {data.missing_cost.length} order{data.missing_cost.length === 1 ? "" : "s"} have no supplier cost on one or more lines
          </p>
          <p className="text-[12px] text-amber-800 mt-0.5">
            Their margin reads high until the cost is entered. Open the order and fill in 🔒 Internal $.
          </p>
          <div className="mt-2.5 max-h-44 overflow-auto rounded-lg bg-white border border-amber-200">
            <table className="w-full text-xs">
              <tbody>
                {data.missing_cost.map((m) => (
                  <tr key={m.order_id} className="border-b border-amber-50 last:border-0">
                    <td className="px-3 py-1.5 font-mono font-bold text-slate-800">{m.order_id}</td>
                    <td className="px-3 py-1.5 text-slate-600">{m.hospital || "—"}</td>
                    <td className="px-3 py-1.5 text-slate-600">{m.supplier || "no supplier recorded"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-800">{fmt$(m.revenue)}</td>
                    <td className="px-3 py-1.5 text-right text-amber-700 font-semibold">
                      {m.lines_missing} of {m.lines} lines
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* breakdown */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center gap-1 px-3 pt-3">
          {[["period", data ? `By ${data.bucket}` : "By period"], ["supplier", "By supplier"],
            ["hospital", "By customer"], ["orders", "Order detail"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`h-8 px-3 rounded-t-lg text-xs font-bold ${
                tab === id ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid={`gp-tab-${id}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gp-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">{tab === "orders" ? "Order" : tab === "period" ? "Period" : "Name"}</th>
                {tab === "orders" && <th className="px-3 py-2 text-left">Date</th>}
                {tab === "orders" && <th className="px-3 py-2 text-left">Customer / supplier</th>}
                {tab !== "orders" && <th className="px-3 py-2 text-right">Orders</th>}
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Supplier cost</th>
                <th className="px-3 py-2 text-right">Gross profit</th>
                <th className="px-3 py-2 text-right">GP %</th>
                <th className="px-3 py-2 text-right">Missing</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r, i) => (
                <tr key={r.key || r.order_id || r.name || i} className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`gp-row-${tab}-${i}`}>
                  <td className="px-3 py-2 font-semibold text-slate-800">
                    {tab === "orders" ? <span className="font-mono">{r.order_id}</span> : (r.label || r.name)}
                  </td>
                  {tab === "orders" && <td className="px-3 py-2 text-slate-500">{r.date}</td>}
                  {tab === "orders" && (
                    <td className="px-3 py-2 text-slate-600">
                      {r.hospital || "—"}
                      <span className="text-slate-400"> · {r.supplier || "no supplier"}</span>
                    </td>
                  )}
                  {tab !== "orders" && <td className="px-3 py-2 text-right tabular-nums text-slate-600">{r.order_count}</td>}
                  <td className="px-3 py-2 text-right tabular-nums">{fmt$(r.revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmt$(r.cost)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{fmt$(r.gp)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${gpTone(r.gp_pct)}`}>{pct(r.gp_pct)}</td>
                  <td className="px-3 py-2 text-right text-xs text-rose-600">
                    {r.missing_cost_orders || r.lines_missing || ""}
                  </td>
                </tr>
              ))}
              {!loading && !(rows || []).length && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400 text-sm">
                  Nothing booked in this window.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-slate-400">
        Orders are counted on the day they shipped, or the day they were created when no ship
        date is recorded. Cancelled orders and zero-value quote requests are excluded.
      </p>
    </div>
  );
}
